import { useContext } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Box, DashPlaceholder, Flex, Text, Button, Image, ModalContext } from "dash-ui";
import { PropertyStatusesEnum, CmsStatusEnum } from "dash-common";

import {
  GetPropertyByIdQuery,
} from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";
import { BadgePropertyStatusChange } from "containers/properties/components/BadgePropertyStatusChange";
import { CmsStatusDropdown } from "containers/properties/components/CmsStatusDropdown";
import { CmsActionButtons } from "containers/properties/components/CmsActionButtons";
import { PropertyDetailsModalsEnum } from "pages/properties/PropertyDetailsPage/PropertyDetailsPage.constants";
import { stickyTopBarStyles } from "containers/properties/helpers/stickyTopBarStyles";

const HeaderWrapper = styled(Box)<{ top?: number }>`
  ${stickyTopBarStyles}
  z-index: 5;
`;

interface IProps {
  property: GetPropertyByIdQuery["getPropertyById"];
  refetch?: () => void;
}

function PropertyDetailsHeader({ property, refetch }: IProps) {
  const { t: tLabels } = useTranslation("common", {
    keyPrefix: "labels",
  });
  const { t: tPropertyPage } = useTranslation("pages", {
    keyPrefix: "propertyPage",
  });

  const { toggleModal, setPayload } = useContext(ModalContext);

  const {
    _id: propertyId,
    primaryOrganizationId,
    name,
    status,
    mainPropertyMediaFile,
    marketArea,
    location,
  } = property;
  const file = mainPropertyMediaFile?.files[0];

  function onDeletePropertyClick() {
    setPayload(property);
    toggleModal(true, PropertyDetailsModalsEnum.DELETE_PROPERTY_MODAL);
  }

  return (
    <HeaderWrapper p={4} w="100%" pos="sticky" top={107}>
      <Flex flexDirection="row" alignItems="center">
        {file ? (
          <Image src={file.thumbnailFile?.publicUrl || ""} size={80} fit="cover" />
        ) : (
          <DashPlaceholder size={80} borderRadius={4} />
        )}

        <Flex flexDirection="column" flex={1} w="100%" ml={3}>
          <Flex flexDirection="column">
            <Text xl bold>
              {name}
            </Text>
          </Flex>
          <Flex flexDirection="row" mt={2} justifyContent="space-between" w="100%">
            <Flex alignItems="center">
              <Button
                mr={3}
                medium
                onClick={() => {
                  window?.open(RoutesPath.PropertyAgentPreview.replace(":id", propertyId), "_blank");
                }}
              >
                Agent Preview
              </Button>
              <Button
                variation="outline"
                mr={3}
                medium
                onClick={() => {
                  window?.open(
                    RoutesPath.PropertyDirectPreview.replace(":id", propertyId),
                    "_blank"
                  );
                }}
              >
                Direct Preview
              </Button>
              <BadgePropertyStatusChange
                currentStatus={status as PropertyStatusesEnum}
                propertyId={propertyId}
                primaryOrganizationId={primaryOrganizationId || undefined}
                marketArea={marketArea}
                location={location}
              />
              <Box ml={2}>
                <CmsStatusDropdown
                  propertyId={propertyId}
                  currentStatus={property.cmsStatus as CmsStatusEnum}
                  onStatusChange={refetch}
                />
              </Box>
              <CmsActionButtons
                propertyId={propertyId}
                currentStatus={property.cmsStatus as CmsStatusEnum}
                onStatusChange={refetch}
              />
              {/* Verified/Unverified toggle hidden for now */}
              {/* <Box ml={4}>
                <Toggle
                  // @ts-ignore
                  color={theme.colors.g20}
                  checked={isMarkedAsVerified}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onSwitchIsMarkedAsVerifiedToggle(e.target.checked);
                  }}
                  label={(isMarkedAsVerified ? tLabels`verified` : tLabels`unverified`) as string}
                  labelProps={{
                    ...(isMarkedAsVerified && {
                      color: theme.colors.g20,
                      bold: true,
                    }),
                  }}
                />
              </Box> */}
            </Flex>
            <Button
              variation="flat"
              onClick={onDeletePropertyClick}
            >{tPropertyPage`deleteProperty`}</Button>
          </Flex>
        </Flex>
      </Flex>
    </HeaderWrapper>
  );
}

export default PropertyDetailsHeader;
